import React, { useState, useMemo, useEffect } from 'react';
import Switch from 'react-switch';
import AudienceModal from './AudienceModal';
import ConfirmSendModal from './ConfirmSendModal';
import SendTestModal from './SendTestModal';
import MessageSection from './MessageSection';
import PreviewSection from './PreviewSection';
import PushNotificationSection from './PushNotificationSection';
import PushPreviewSection from './PushPreviewSection';
import AudienceSection from './AudienceSection';
import AnnouncementsActions from './AnnouncementsActions';
import { announceMessage, previewAudienceCount } from '../../api/chatMessages';
import './Announcements.scss';

const HIGH_RECIPIENT_THRESHOLD = 10000;

const Announcements = () => {
  const [messageHeadline, setMessageHeadline] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [pushHeadline, setPushHeadline] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [audienceIdGroups, setAudienceIdGroups] = useState([]);
  const [isAudienceModalOpen, setIsAudienceModalOpen] = useState(false);
  const [audienceState, setAudienceState] = useState({ count: null, loading: false, error: null });
  const [isSending, setIsSending] = useState(false);
  const [isConfirmSendOpen, setIsConfirmSendOpen] = useState(false);
  const [isSendTestOpen, setIsSendTestOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendEmailManuallySet, setSendEmailManuallySet] = useState(false);

  const recipientCount = audienceState.count ?? 0;
  const isHighRecipientCount = recipientCount > HIGH_RECIPIENT_THRESHOLD;

  const audience = useMemo(() => {
    const tagIds = audienceIdGroups.filter(group => group.tagIds.length > 0).map(group => group.tagIds);
    if (tagIds.length === 0) return null;
    return { tagIds };
  }, [audienceIdGroups]);

  useEffect(() => {
    if (!audience) {
      setAudienceState(prev => ({ ...prev, count: null, error: null }));
      return;
    }

    setAudienceState(prev => ({ ...prev, loading: true, error: null }));
    previewAudienceCount(audience)
      .then(data => setAudienceState(prev => ({ ...prev, count: data.count })))
      .catch(err => {
        setAudienceState(prev => ({
          ...prev,
          error: err?.message || err || 'Failed to fetch audience count',
          count: null
        }));
      })
      .finally(() => setAudienceState(prev => ({ ...prev, loading: false })));
  }, [audience]);

  useEffect(() => {
    if (!sendEmailManuallySet) {
      setSendEmail(!isHighRecipientCount);
    }
  }, [isHighRecipientCount, sendEmailManuallySet]);

  const handleSendEmailToggle = () => {
    if (!sendEmail && isHighRecipientCount) {
      const confirmed = window.confirm(
        `You are about to enable email sending for ${recipientCount.toLocaleString()} recipients. This will send a large volume of emails. Are you sure?`
      );
      if (!confirmed) return;
    }
    setSendEmail(prev => !prev);
    setSendEmailManuallySet(true);
  };

  const handleSendTest = () => {
    setIsSendTestOpen(true);
  };

  const handleSendTestToUsers = users => {
    const userIds = users.map(u => u.id);
    sendAnnouncement({ userIds }, true);
  };

  const canSendTest = Boolean(messageHeadline.trim()) && Boolean(messageBody.trim()) && Boolean(pushHeadline.trim()) && Boolean(pushBody.trim());

  const canSend = recipientCount > 0 && canSendTest;

  const handleSendMessageClick = () => {
    setIsConfirmSendOpen(true);
  };

  const handleConfirmSend = () => {
    sendAnnouncement(audience, false);
  };

  const sendAnnouncement = (audienceOverride, isTest) => {
    setIsSending(true);
    announceMessage({
      messageHeadline: messageHeadline.trim(),
      messageBody: messageBody.trim(),
      pushHeadline: pushHeadline.trim(),
      pushBody: pushBody.trim(),
      attachmentUrl: attachmentUrl ? attachmentUrl.trim() : undefined,
      sendEmail,
      audience: audienceOverride
    })
      .then(() => {
        // reset fields
        setMessageHeadline('');
        setMessageBody('');
        setAttachmentUrl(null);
        setAttachmentFile(null);
        setPushHeadline('');
        setPushBody('');
        setAudienceIdGroups([]);
        setSendEmail(true);
        setSendEmailManuallySet(false);
        window.ALERT.success(isTest ? 'Test announcement sent successfully.' : 'Announcement sent successfully.');
      })
      .catch(err => {
        window.ALERT.error(isTest ? 'Failed to send test announcement.' : 'Failed to send announcement.');
        console.error(err);
      })
      .finally(() => setIsSending(false));
  };

  const handleAudienceSelect = idGroups => {
    setAudienceIdGroups(idGroups);
  };

  const audienceLabel = useMemo(() => audienceIdGroups.map(group => `(${group.labels.join(' AND ')})`).join(' OR '), [audienceIdGroups]);

  return (
    <div className='announcements-outer'>
      <div className='announcements-inner'>
        <h1 className='announcements-title'>Announcements</h1>

        <MessageSection
          messageHeadline={messageHeadline}
          onMessageHeadlineChange={setMessageHeadline}
          messageBody={messageBody}
          onMessageBodyChange={setMessageBody}
          attachmentUrl={attachmentUrl}
          onAttachmentUrlChange={setAttachmentUrl}
          onAttachmentFileChange={setAttachmentFile}
        />

        <PreviewSection messageHeadline={messageHeadline} messageBody={messageBody} attachmentFile={attachmentFile} />

        <PushNotificationSection
          pushHeadline={pushHeadline}
          onPushHeadlineChange={setPushHeadline}
          pushBody={pushBody}
          onPushBodyChange={setPushBody}
        />

        <PushPreviewSection pushHeadline={pushHeadline} pushBody={pushBody} />

        <AudienceSection
          audienceLabel={audienceLabel}
          onSelectClick={() => setIsAudienceModalOpen(true)}
          isLoading={audienceState.loading}
          error={audienceState.error}
          userCount={audienceState.count}
        />

        <div className='announcements-section'>
          <div className='announcements-email-toggle'>
            <div className='announcements-email-toggle-label'>
              <Switch
                onChange={handleSendEmailToggle}
                checked={sendEmail}
                height={14}
                width={24}
                onColor='#333'
                offColor='#ccc'
                checkedIcon={false}
                uncheckedIcon={false}
                className='switch'
              />
              <span>Send Email</span>
            </div>
            {isHighRecipientCount && !sendEmail && (
              <p className='announcements-email-toggle-hint'>
                Email sending is off by default for large audiences ({recipientCount.toLocaleString()} recipients).
              </p>
            )}
          </div>
        </div>

        <AnnouncementsActions
          onSendTest={handleSendTest}
          onSendMessage={handleSendMessageClick}
          canSendTest={canSendTest}
          canSend={canSend}
          isSending={isSending}
        />
      </div>

      <AudienceModal isOpen={isAudienceModalOpen} onClose={() => setIsAudienceModalOpen(false)} onSelect={handleAudienceSelect} />

      <ConfirmSendModal
        isOpen={isConfirmSendOpen}
        onClose={() => setIsConfirmSendOpen(false)}
        onConfirm={handleConfirmSend}
        recipientCount={recipientCount}
      />

      <SendTestModal isOpen={isSendTestOpen} onClose={() => setIsSendTestOpen(false)} onSelectUsers={handleSendTestToUsers} />
    </div>
  );
};

export default Announcements;
